<template>
  <div :key="`incident-menu-${incidentId}`" ref="el" @click="onClick">
    <item-header type="incident" :choosable="true" :selected-id="incidentId" @select="selectIncident" @close="$emit('close')">
      <editable-item-title :item="incident" />
    </item-header>

    <drawer
      v-model:open="drawerStates.incidentGeneral"
      :title="$t('labels.general')"
    >
      <div class="column gap-4">
        <input-text
          ref="title"
          :label="$t('labels.title')"
          :value="incident.title"
          @enter="descriptionField?.focus()"
          @update:value="updateKey('title', $event)"
        />

        <input-texteditor
          ref="descriptionField"
          :value="incident.description"
          :label="$t('labels.description')"
          @update:value="updateKey('description', $event)"
        />
      </div>
    </drawer>

    <drawer
      v-model:open="drawerStates.incidentEffects"
      :title="$t('objectNames.effect', 2)"
    >
      <effect-list
        ref="effectList"
        :effects="effects"
        :selected-effect="
          subSelection?.type == 'effect'
            ? { type: 'effect', id: subSelection.id }
            : undefined
        "
        @copy="emit('copy', $event)"
        @select="select"
      >
        <item-combo-select
          key="incidentmenu-effect-select"
          :selection="affectedItems"
          :available-ids="availableEffectElementIds"
          :targets="targets"
          :resources="resources"
          :indices="indices"
          :hide-selected="true"
          @select-single="onAffectedItemChoose"
          @choose="onAffectedItemsChoose"
        />
      </effect-list>
    </drawer>

    <drawer
      v-model:open="drawerStates.incidentTriggers"
      :title="$t('objectNames.trigger', 2)"
    >
      <trigger-item-list
        :incident-id="incident.id"
        @select="select"
        @deselect="deselect"
        @copy="emit('copy', $event)"
      />
    </drawer>

    <drawer
      v-model:open="drawerStates.incidentDependingIncidents"
      :title="$t('objectNames.dependentIncident', 2)"
    >
      <depending-incident-triggers
        :activator="incident"
        :selected-condition="selectedCondition"
        @select="onDependingIncidentSelect($event)"
      />
    </drawer>

    <drawer
      v-model:open="drawerStates.incidentScenarios"
      :title="$t('objectNames.scenario', 2)"
    >
      <div class="column">
        <input-checkbox
          v-for="scenario of scenarios"
          :key="scenario.id"
          :label="scenario.title"
          :value="scenario.incidentIds.includes(incident.id)"
          @update:value="
            $event
              ? scenarioStore.connectIncident({
                  id: scenario.id,
                  incidentId: incident.id
                })
              : scenarioStore.disconnectIncident({
                  id: scenario.id,
                  incidentId: incident.id
                })
          "
        />
      </div>
    </drawer>

    <drawer
      v-model:open="drawerStates.incidentVisibility"
      :title="$t('labels.visibility')"
    >
      <input-checkbox
        :value="incident.visibility == 'visible'"
        @update:value="updateKey('visibility', $event ? 'visible' : 'hidden')"
      >
        {{ $t("labels.incidentVisible") }}
      </input-checkbox>

      <h3 class="label mt-5">{{ $t("labels.effectVisibility") }}</h3>
      <div class="column gap-1">
        <input-radio
          :value="incident.effectsVisibility"
          :data="EFFECTS_VISIBILITY.asIs"
          :label="$t('labels.effectAsIs')"
          @update:value="updateKey('effectsVisibility', $event)"
        />
        <input-radio 
          :value="incident.effectsVisibility"
          :data="EFFECTS_VISIBILITY.visible"
          :label="$t('labels.effectForceVisible')"
          @update:value="updateKey('effectsVisibility', $event)"
        />
        <input-radio 
          :value="incident.effectsVisibility"
          :data="EFFECTS_VISIBILITY.valuesHidden"
          :label="$t('labels.effectValuesHidden')"
          @update:value="updateKey('effectsVisibility', $event)"
        />
        <input-radio 
          :value="incident.effectsVisibility"
          :data="EFFECTS_VISIBILITY.hidden"
          :label="$t('labels.effectForceHidden')"
          @update:value="updateKey('effectsVisibility', $event)"
        />
      </div>
    </drawer>

    <drawer
      v-if="features.design"
      v-model:open="drawerStates.incidentDesign"
      :title="$t('labels.design')"
    >
      <input-image
        :id="incident.imageId ?? undefined"
        :max-width="1000"
        :max-height="1000"
        :compression="0.9"
        :control-object-fit="true"
        :target-width="325"
        :target-height="520"
        @update="setImage"
        @delete="deleteImage"
      />
    </drawer>

    <menu-group>
      <button
        class="button button_secondary mt-2"
        type="button"
        @click="deleteIncident"
      >
        {{ $t("actions.deleteIncident", 1) }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch, useTemplateRef } from "vue";
import { notNull } from "@/helpers/utility/Type";
import { map } from "lodash-es";

import MenuGroup from "@core/ui/MenuGroup.vue";
import Drawer from "@core/ui/DrawerBlock.vue";
import ItemHeader from "@editor/components/ItemHeader.vue";

import InputText from "@core/input/InputText.vue";
import InputRadio from "@core/input/RadioButton.vue";
import InputTexteditor from "@core/input/TextEditor.vue";
import InputImage from "@editor/items/image/InputImage.vue";
import ItemComboSelect from "@editor/ui/ItemComboSelect.vue";

import InputCheckbox from "@core/input/InputCheckbox.vue";
import EffectList from "@editor/items/effect/EffectList.vue";
import TriggerItemList from "../incidentTrigger/TriggerItemList.vue";
import EditableItemTitle from "@editor/components/EditableItemTitle.vue";
import DependingIncidentTriggers from "./DependingIncidentTriggers.vue";

import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useEffectStore } from "@editor/items/effect/effects.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useFeatures } from "@editor/store/features.store";
import { useScenarioStore } from "../scenario/scenarios.store";
import { useVariablesViewStore } from "@editor/views/variables/variablesView.store";

import type { IncidentItem } from "./IncidentItem";
import type { EffectItem } from "../effect/EffectItem";
import type { ConditionItem } from "../condition/ConditionItem";
import type { IncidentTriggerItem } from "../incidentTrigger/IncidentTriggerItem";

interface Props {
  incidentId: number;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (
    event: "select",
    item: EffectItem | ConditionItem | IncidentTriggerItem | null
  ): void;
  (
    event: "deselect",
    item: IncidentItem | EffectItem | ConditionItem | IncidentTriggerItem
  ): void;
  (event: "select-main", item: IncidentItem): void;
  (event: "click", mouseEvent: MouseEvent): void;
  (event: "delete"): void;
  (event: "close"): void;
  (event: "copy", item: EffectItem | IncidentTriggerItem): void;
}>();

const features = useFeatures();
const drawerStates = useDrawerStates();
const scenarioStore = useScenarioStore();

const el = ref<HTMLElement>();
const titleField = useTemplateRef("title");
const descriptionField = useTemplateRef("descriptionField");
const effectList = useTemplateRef("effectList");

const incident = computed(() => useIncidentStore().items[props.incidentId]);
const scenarios = computed(() => scenarioStore.orderedItems);
const targets = computed(() => useTargetStore().items);
const resources = computed(() => useResourceStore().items);
const indices = computed(() => useIndexStore().items);
const effects = computed(() => useEffectStore().byIncident[props.incidentId] ?? []);

const affectedItems = computed(() => {
  return effects.value
    .map((effect) => {
      if (effect.affectedType === "resource") {
        return resources.value[effect.affectedId];
      } else if (effect.affectedType === "target") {
        return targets.value[effect.affectedId];
      }
    })
    .filter(notNull);
});
const subSelection = computed(() => {
  return useVariablesViewStore().subSelection;
});
const selectedCondition = computed(() => {
  return subSelection.value && subSelection.value.type == "condition"
    ? (subSelection.value as { type: "condition"; id: number })
    : undefined;
});

const availableEffectElementIds = computed(() => {
  return {
    target: map(targets.value, (target) => target.id),
    resource: map(resources.value, (resource) => resource.id),
    index: []
  }
});

watch(
  () => props.incidentId,
  () => {
    if (el.value != null) focusTitleIfEmpty();
  },
  { immediate: true }
);

onMounted(() => focusTitleIfEmpty());

function onClick(mouseEvent: MouseEvent) {
  emit("click", mouseEvent);
}

function focusTitleIfEmpty() {
  if (incident.value.title.length === 0) {
    drawerStates.incidentGeneral = true;
    nextTick(() => {
      titleField.value?.focus();
    });
  }
}
function deselect(
  item:
    | IncidentItem
    | EffectItem
    | IncidentItem
    | ConditionItem
    | IncidentTriggerItem
) {
  emit("deselect", item);
}
function select(
  item: EffectItem | ConditionItem | IncidentItem | IncidentTriggerItem | null
) {
  if (item?.type == "incident") {
    emit("select-main", item);
  } else {
    emit("select", item);
  }
}

function selectIncident(incidentId: number | null) {
  if (incidentId === props.incidentId) {
    return;
  }
  if (incidentId !== null) {
    emit("select-main", useIncidentStore().items[incidentId]);
  }
}

function updateKey<K extends keyof IncidentItem>(key: K, value: IncidentItem[K]) {
  useIncidentStore().update({
    id: props.incidentId,
    [key]: value
  });
}
function deleteIncident() {
  emit("delete");
  useIncidentStore().delete({
    id: props.incidentId
  });
}
function setImage(image: { id: number }) {
  updateKey("imageId", image.id);
}
function deleteImage() {
  updateKey("imageId", null);
}

async function onAffectedItemChoose(item: {
  type: "target" | "resource";
  id: number;
}) {
  const effect = await useEffectStore().create({
    activatorType: incident.value.type,
    activatorId: incident.value.id,
    affectedType: item.type,
    affectedId: item.id
  });
  if (effectList.value) {
    effectList.value.focusEffectExpressionField(effect);
  }
}

function onAffectedItemsChoose(
  items: { type: "target" | "resource"; id: number }[]
) {
  useEffectStore().connectActivatorEffects(incident.value, items);
}

function onDependingIncidentSelect(item: ConditionItem | IncidentItem) {
  if (item.type == "condition") {
    emit("select", item);
  } else {
    useDrawerStates().incidentTriggers = true;
    emit("select-main", item);
  }
}

const EFFECTS_VISIBILITY = {
  asIs: "as-is",
  hidden: "hidden",
  visible: "visible",
  valuesHidden: "values-hidden"
} as const;
</script>
